import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { isForbidden, isNotFound, type MediaObject, type SupportTicket, type SupportTicketDetail } from '@localagent/sdk';
import { authIdentityAtom, holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import { getSDK } from '../services/sdk-instance';
import { SupportInbox } from './SupportInbox';
import { SupportDetail } from './SupportDetail';

export interface SupportViewProps {
  tickets?: SupportTicket[];
  detail?: SupportTicketDetail;
  operator?: boolean;
  artifacts?: MediaObject[];
}

async function detectOperator(): Promise<boolean> {
  try {
    await getSDK().support.adminList({ limit: 1 });
    return true;
  } catch (err) {
    if (isNotFound(err) || isForbidden(err)) {
      return false;
    }
    throw err;
  }
}

export function SupportView(props: SupportViewProps = {}) {
  const { id } = useParams<{ id?: string }>();
  const fixtureMode = props.tickets !== undefined || props.detail !== undefined;
  const configured = useAtomValue(sdkConfiguredAtom);
  const signedIn = useAtomValue(holdsIdentityAtom);
  const identity = useAtomValue(authIdentityAtom);
  const live = configured && signedIn && !fixtureMode;

  const operatorQuery = useQuery({
    queryKey: ['support', 'operator'],
    queryFn: detectOperator,
    enabled: live && props.operator === undefined,
  });
  const operator = props.operator ?? operatorQuery.data === true;
  const operatorSettled =
    props.operator !== undefined || !live || operatorQuery.isFetched || operatorQuery.isError;

  // Occupy the dock leftover lane the way WorkView does — `h-full` alone
  // collapses the inbox list to 0 under a bottom-docked Shogun panel.
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {id ? (
        <SupportDetail
          ticketId={id}
          fixture={props.detail}
          operator={operator}
          artifacts={props.artifacts}
          live={live}
          userId={identity?.user_id}
        />
      ) : (
        <SupportInbox
          fixtureTickets={props.tickets}
          operator={operator}
          operatorSettled={operatorSettled}
          operatorError={operatorQuery.error}
          live={live}
          userId={identity?.user_id}
        />
      )}
    </div>
  );
}
